import React from "react";
import WidgetCard from "@ui/WidgetCard";
import Statistic from "@ui/Statistic";
import t from "../../../../../i18n";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import BlankStateV2 from "@ui/BlankStateV2";
import { type Quantity } from "../../../../../api/useQuantities";
import type { Project } from "../../../../../api/useProjects";
import { isAnalysisQuantityValid } from "../../../metrology";
const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 20px;
`;

const StatisticsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-gap: 24px;
`;

const statisticsStyled = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  white-space: nowrap;
`;

interface P {
  project: Project;
  activeAnalyses: ActiveAnalysis;
  processIndicators: Quantity[];
}

export function ProcessIndicators({ project: _project, activeAnalyses, processIndicators }: P) {
  const indicatorQuantityValues = React.useMemo(() => {
    const validProcessIndicators = processIndicators.filter(quantity =>
      isAnalysisQuantityValid(activeAnalyses, quantity),
    );

    return Object.fromEntries(
      Object.entries(activeAnalyses.quantityValues).filter(([key]) =>
        validProcessIndicators.some(q => q.id === key),
      ),
    );
  }, [processIndicators, activeAnalyses]);

  const indicators = React.useMemo(
    () =>
      processIndicators.map(indicator => {
        const measured = indicatorQuantityValues[indicator.id]?.measured;
        const hasValue = measured !== undefined;

        return {
          id: indicator.id,
          name: indicator.name,
          measured,
          unit: hasValue ? (indicator.unit ?? undefined) : undefined,
        };
      }),
    [indicatorQuantityValues, processIndicators],
  );

  return (
    <WidgetCard
      title={t("commom.widgets.processIndicators.title")}
      data-cy="processIndicators-section"
    >
      {processIndicators.length === 0 ? (
        <StyledBlankStateV2
          title={t("common.processIndicators.blank.title")}
          subtitle={t("common.processIndicators.blank.title.description")}
        />
      ) : (
        <StatisticsWrapper>
          {indicators.flatMap(qoi => [
            <Statistic
              key={qoi.id}
              className={statisticsStyled}
              title={qoi.name}
              value={qoi.measured}
              suffix={qoi.unit}
              precision={1}
            />,
          ])}
        </StatisticsWrapper>
      )}
    </WidgetCard>
  );
}

export default React.memo(ProcessIndicators);
